import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const API = '/api/digest-preferences';

export default function DigestPreferences() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const action = searchParams.get('action');

  const [status, setStatus] = useState({ loading: true, error: null, subscribed: null, email: null, done: null });
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus({ loading: false, error: 'missing', subscribed: null, email: null, done: null });
      return;
    }
    if (action === 'unsubscribe') {
      setStatus(s => ({ ...s, loading: true }));
      fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, action: 'unsubscribe' })
      })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setStatus({ loading: false, error: null, subscribed: false, email: data.email, done: 'unsubscribed' });
          } else {
            setStatus({ loading: false, error: data.error || 'Failed to unsubscribe', subscribed: null, email: null, done: null });
          }
        })
        .catch(() => setStatus({ loading: false, error: 'Something went wrong.', subscribed: null, email: null, done: null }));
      return;
    }
    if (action === 'subscribe') {
      setStatus(s => ({ ...s, loading: true }));
      fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, action: 'subscribe' })
      })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setStatus({ loading: false, error: null, subscribed: true, email: data.email, done: 'subscribed' });
          } else {
            setStatus({ loading: false, error: data.error || 'Failed to subscribe', subscribed: null, email: null, done: null });
          }
        })
        .catch(() => setStatus({ loading: false, error: 'Something went wrong.', subscribed: null, email: null, done: null }));
      return;
    }
    fetch(`${API}/status?token=${encodeURIComponent(token)}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setStatus({ loading: false, error: null, subscribed: data.subscribed, email: data.email, done: null });
        } else {
          setStatus({ loading: false, error: data.error || 'Invalid link', subscribed: null, email: null, done: null });
        }
      })
      .catch(() => setStatus({ loading: false, error: 'Something went wrong.', subscribed: null, email: null, done: null }));
  }, [token, action]);

  const handleToggle = (newSubscribed) => {
    if (!token || toggling) return;
    setToggling(true);
    fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, action: newSubscribed ? 'subscribe' : 'unsubscribe' })
    })
      .then(res => res.json())
      .then(data => {
        setToggling(false);
        if (data.success) setStatus(s => ({ ...s, subscribed: data.subscribed, done: data.subscribed ? 'subscribed' : 'unsubscribed' }));
      })
      .catch(() => setToggling(false));
  };

  if (status.loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #e5e7eb', borderTopColor: '#06b6d4', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#6b7280', margin: 0 }}>Updating your preferences…</p>
        </div>
      </div>
    );
  }

  if (status.error === 'missing' || !token) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: 24 }}>
        <div style={{ maxWidth: 400, background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <h1 style={{ margin: '0 0 12px', fontSize: 22, color: '#111827' }}>Manage digest subscription</h1>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 15 }}>Use the <strong>Unsubscribe</strong> link at the bottom of your latest Weekly Trend Digest email to manage your subscription. That link is unique to you.</p>
          <p style={{ margin: '16px 0 0', fontSize: 14, color: '#9ca3af' }}>If you didn’t receive an email, check spam or contact support.</p>
        </div>
      </div>
    );
  }

  if (status.error && !status.done) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: 24 }}>
        <div style={{ maxWidth: 400, background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <h1 style={{ margin: '0 0 12px', fontSize: 22, color: '#111827' }}>Invalid or expired link</h1>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 15 }}>{status.error}</p>
          <p style={{ margin: '16px 0 0', fontSize: 14, color: '#9ca3af' }}>Use the link from your most recent digest email, or request a new one.</p>
        </div>
      </div>
    );
  }

  const subscribed = status.subscribed === true;
  const done = status.done;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: 24 }}>
      <div style={{ maxWidth: 420, background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: 24, color: '#111827' }}>Weekly Trend Digest</h1>
        {status.email && <p style={{ margin: '0 0 24px', fontSize: 14, color: '#6b7280' }}>{status.email}</p>}
        {done === 'unsubscribed' && <p style={{ margin: '0 0 24px', fontSize: 16, color: '#059669', fontWeight: 600 }}>You’re unsubscribed. You won’t receive the weekly digest email.</p>}
        {done === 'subscribed' && <p style={{ margin: '0 0 24px', fontSize: 16, color: '#059669', fontWeight: 600 }}>You’re subscribed. You’ll receive the digest every Sunday at 12p PT.</p>}
        {!done && <p style={{ margin: '0 0 24px', fontSize: 15, color: '#374151' }}>{subscribed ? 'You’re currently subscribed to the Weekly Trend Digest.' : 'You’re not subscribed to the Weekly Trend Digest.'}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
          {subscribed ? (
            <button
              type="button"
              onClick={() => handleToggle(false)}
              disabled={toggling}
              style={{
                padding: '12px 24px',
                background: '#fff',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                fontWeight: 600,
                cursor: toggling ? 'not-allowed' : 'pointer',
                fontSize: 15
              }}
            >
              {toggling ? 'Updating…' : 'Unsubscribe'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleToggle(true)}
              disabled={toggling}
              style={{
                padding: '12px 24px',
                background: '#06b6d4',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                cursor: toggling ? 'not-allowed' : 'pointer',
                fontSize: 15
              }}
            >
              {toggling ? 'Updating…' : 'Subscribe again'}
            </button>
          )}
        </div>
        <p style={{ margin: '24px 0 0', fontSize: 12, color: '#9ca3af' }}>ShowStop · Weekly digest every Sunday at 12p PT</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
